import React, { useState } from 'react';
import StarterTemplates from './StarterTemplates';
import styled from 'styled-components';
import { push, ref, set } from 'firebase/database';
import { db } from '../../util/firebase';

const OnboardingContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 32px 20px;
`;

const OnboardingShell = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const IntroCard = styled.div`
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 28px;
  margin-bottom: 20px;
  text-align: center;
`;

const Title = styled.h1`
  color: #2d3748;
  font-size: 2rem;
  margin-bottom: 10px;
`;

const Subtitle = styled.p`
  color: #718096;
  font-size: 1rem;
  line-height: 1.6;
  max-width: 620px;
  margin: 0 auto 22px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  background: #0078D4;
  color: white;
  border: 1px solid #0078D4;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  padding: 12px 18px;

  &:disabled {
    background: #a0aec0;
    border-color: #a0aec0;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  background: white;
  color: #0078D4;
  border: 1px solid #0078D4;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  padding: 12px 18px;
`;

const ErrorMessage = styled.p`
  color: #dc3545;
  margin: 14px 0 0;
`;

const OnboardingFlow = ({ user, onComplete }) => {
  const FREE_TASK_LIMIT = 5;
  const [showTemplates, setShowTemplates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const finishOnboarding = () => {
    if (user?.uid) {
      localStorage.setItem(`rundoit_onboarding_completed_${user.uid}`, 'true');
    }
    onComplete();
  };

  const importTemplate = async (template) => {
    if (!user?.uid) {
      finishOnboarding();
      return;
    }

    try {
      setError('');
      setSaving(true);
      const todoListRef = ref(db, `users/${user.uid}/todoList`);

      const tasksToAdd = template.tasks.slice(0, FREE_TASK_LIMIT);

      await Promise.all(tasksToAdd.map((title, index) => {
        const newTaskRef = push(todoListRef);
        return set(newTaskRef, {
          title,
          templateId: template.id,
          templateName: template.name,
          order: index,
          createdAt: Date.now()
        });
      }));

      finishOnboarding();
    } catch (err) {
      console.error('Unable to import starter template:', err);
      setError('Could not add that template. Please try again.');
      setSaving(false);
    }
  };

  if (showTemplates) {
    return (
      <OnboardingContainer>
        <OnboardingShell>
          <StarterTemplates
            title="Start with a template"
            subtitle="Pick one list to add to your dashboard. Free accounts start with up to 5 template tasks."
            onSelectTemplate={importTemplate}
            onCreateCustom={finishOnboarding}
          />
          <ButtonRow>
            <SecondaryButton type="button" onClick={finishOnboarding} disabled={saving}>
              Skip for now
            </SecondaryButton>
          </ButtonRow>
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </OnboardingShell>
      </OnboardingContainer>
    );
  }

  return (
    <OnboardingContainer>
      <OnboardingShell>
        <IntroCard>
          <Title>Welcome to Rundoit</Title>
          <Subtitle>
            Add a few tasks and use Random, Start, or Flow mode to decide what to do next.
            You can begin with a template or start with an empty list.
          </Subtitle>
          <ButtonRow>
            <PrimaryButton type="button" onClick={() => setShowTemplates(true)}>
              Choose a template
            </PrimaryButton>
            <SecondaryButton type="button" onClick={finishOnboarding}>
              Start empty
            </SecondaryButton>
          </ButtonRow>
        </IntroCard>
      </OnboardingShell>
    </OnboardingContainer>
  );
};

export default OnboardingFlow;
